import { NativeModules, Platform } from "react-native";

type NativeIntercom = {
  loginUserWithUserAttributes: (params: {
    userId?: string;
    email?: string;
  }) => Promise<boolean>;
  updateUser: (params: {
    userId?: string;
    email?: string;
    name?: string;
    phone?: string;
    signedUpAt?: number;
    customAttributes?: Record<string, any>;
  }) => Promise<boolean>;
  logout: () => Promise<boolean>;
  setLauncherVisibility: (visibility: "GONE" | "VISIBLE") => Promise<boolean>;
  setInAppMessageVisibility: (
    visibility: "GONE" | "VISIBLE"
  ) => Promise<boolean>;
};

let nativeSessionStarted = false;

const getNativeIntercom = (): NativeIntercom | null => {
  if (Platform.OS === "web") return null;
  if (!NativeModules.IntercomModule) return null;
  try {
    return require("@intercom/intercom-react-native").default;
  } catch {
    return null;
  }
};

const signedUpAtSeconds = (createdAt?: string) => {
  if (!createdAt) return undefined;
  const numeric = Number(createdAt);
  if (Number.isFinite(numeric) && numeric > 0) {
    return numeric > 1_000_000_000_000 ? Math.floor(numeric / 1000) : numeric;
  }
  const parsed = Date.parse(createdAt);
  if (Number.isNaN(parsed)) return undefined;
  return Math.floor(parsed / 1000);
};

const hideNativeMessenger = () => {
  const Intercom = getNativeIntercom();
  if (!Intercom) return;
  Intercom.setLauncherVisibility("GONE").catch(() => undefined);
  Intercom.setInAppMessageVisibility("GONE").catch(() => undefined);
};

export default {
  // Native startup already calls initialize(). Keep the UI hidden until login
  // so packages whose users have no show_intercom_key never surface Intercom.
  suppressUntilLogin: () => {
    if (Platform.OS === "web" || nativeSessionStarted) return;
    hideNativeMessenger();
  },

  loginUser: (
    app_id: string,
    userId: string,
    email?: string,
    name?: string,
    created_at?: string,
    custom_data?: Record<string, any>,
    phone?: string
  ) => {
    if (!app_id) return;

    if (Platform.OS !== "web") {
      const Intercom = getNativeIntercom();
      if (!Intercom || (!userId && !email)) return;

      const attributes = {
        userId,
        email,
        name,
        phone,
        signedUpAt: signedUpAtSeconds(created_at),
        customAttributes: custom_data,
      };

      nativeSessionStarted = true;
      Intercom.loginUserWithUserAttributes({ userId, email })
        .then(() => Intercom.updateUser(attributes))
        .then(() => Intercom.setInAppMessageVisibility("VISIBLE"))
        .catch(() => undefined);
      return;
    }

    (window as any).intercomSettings = {
      app_id,
      user_id: userId,
      email,
      name,
      created_at,
      custom_data,
    };
    if (!document.getElementById("intercom-script")) {
      const script = document.createElement("script");
      script.id = "intercom-script";
      script.src = `https://widget.intercom.io/widget/${app_id}`;
      script.async = true;

      script.onload = () => {
        (window as any).Intercom?.("boot", (window as any).intercomSettings);
      };

      document.body.appendChild(script);
    } else {
      (window as any).Intercom?.("boot", (window as any).intercomSettings);
    }
  },

  logout: () => {
    if (Platform.OS !== "web") {
      if (!nativeSessionStarted) return;
      nativeSessionStarted = false;
      hideNativeMessenger();
      getNativeIntercom()
        ?.logout()
        .catch(() => undefined);
      return;
    }
    (window as any).Intercom?.("shutdown");
  },
};
